import React, { FunctionComponent, useEffect, useRef, useState } from "react";
import { SideMenuPageItems } from "components/molecules/menu/SideMenuPageItems";
import SideMenuUserItems from "components/molecules/menu/SideMenuUserItems";
import { useNavigate } from "react-router-dom";
import { MenuItem } from "interfaces/menu/menu";
import styles from "components/molecules/menu/SideMenu/sideMenu.module.scss";
import { HOME_PATH } from "constants/routes/RoutePaths";
import { FEEDBACK_LINK, MENU_ITEMS, MENU_ITEMS_USER } from "constants/menu/menu";
import { Dashboard, KeyboardTab } from "@mui/icons-material";
import Box from "@mui/material/Box";
import { BorderButton } from "components/atoms/button/BorderButton";
import { useIntl } from "react-intl";
import { useMediaQueryUtil } from "hooks/styles/useMediaQuery";

interface SideMenuProps {
  onReduce: () => void;
  mobileMenuOpen: boolean;
  mobile: boolean;
  onCloseMobileMenu?: () => void;
  reduce?: boolean;
  className?: string;
}

const homeMenuItem = {
  Icon: Dashboard,
  label: "menu_home",
  href: HOME_PATH,
};

const findParent = (items: MenuItem[]): MenuItem =>
  items.find((item) => item.subItems?.find((sub) => sub.parent === item.label));

const findMatchingPath = (items: MenuItem[]): MenuItem => items?.find((item) => location?.pathname === item.href);

const initialMenuItem = (): MenuItem => {
  if (initiaSublMenuItem()) {
    return findParent(MENU_ITEMS) || findParent(MENU_ITEMS_USER);
  }
  return (findMatchingPath(MENU_ITEMS) || findMatchingPath(MENU_ITEMS_USER)) ?? homeMenuItem;
};

const initiaSublMenuItem = (): MenuItem => {
  return (
    (MENU_ITEMS.flatMap((item) => item.subItems).find((item) => location.pathname === item?.href) ||
      MENU_ITEMS_USER.flatMap((item) => item.subItems).find((item) => location.pathname === item?.href)) ??
    undefined
  );
};
const SideMenu: FunctionComponent<SideMenuProps> = ({
  onReduce,
  reduce,
  mobileMenuOpen,
  mobile,
  onCloseMobileMenu,
}) => {
  const navigate = useNavigate();
  const intl = useIntl();
  const { smDown } = useMediaQueryUtil();
  const cNames = `${styles["side-menu"]} ${reduce ? styles.reduce : ""} ${
    mobile && !mobileMenuOpen ? styles["mobile-closed"] : ""
  }`;
  const showSidebarItems = (mobile && mobileMenuOpen) || !mobile;
  const handleOverlayOnClick = () => {
    onCloseMobileMenu();
  };

  const [selectedMenuItem, setSelectedMenuItem] = useState<MenuItem>(initialMenuItem());
  const [selectedSubMenuItem, setSelectedSubMenuItem] = useState<MenuItem>(initiaSublMenuItem());
  const [subMenuOpen, setSubMenuOpen] = useState(false);

  const handleSelectMenuItem = (item: MenuItem) => {
    setSelectedMenuItem(item);
  };
  const handleSelectSubMenuItem = (item: MenuItem) => {
    setSelectedSubMenuItem(item);
  };
  const handleOpenSubMenu = (open: boolean) => {
    setSubMenuOpen(open);
  };

  const handleOnSelect = (item: MenuItem) => {
    // set menu item
    handleSelectMenuItem(item);
    // if clicked on a sub-item - set subItem state and don't continue further
    if (item?.isSubItem) {
      handleSelectSubMenuItem(item);
      return;
    }
    if (item?.subItems) {
      // if clicked on a menu item that has sub-items - open sub-menu
      handleOpenSubMenu(!subMenuOpen);
    } else {
      // else - reset any previously selected sub-menus and close sub-menu panel
      handleSelectSubMenuItem(undefined);
      handleOpenSubMenu(false);
    }
  };

  // initial render state, check if there's a url that matches a sub-item, and if it does, open the sub-menu panel
  useEffect(() => {
    if (initiaSublMenuItem()) {
      handleOpenSubMenu(true);
    }
  }, []);

  useEffect(() => {
    // when selecting a parent menu item that has sub-items for the first time, pre-select the first item in the sub-menu list
    if (selectedMenuItem?.subItems && !selectedSubMenuItem) {
      navigate(selectedMenuItem.subItems[0].href);
      handleSelectSubMenuItem(selectedMenuItem.subItems[0]);
    }
  }, [selectedMenuItem]);

  return (
    <>
      <Box className={cNames}>
        {showSidebarItems && (
          <>
            <SideMenuPageItems
              mobileMenuOpen={mobileMenuOpen}
              reduce={reduce}
              onCloseMobileMenu={onCloseMobileMenu}
              selectedItem={selectedMenuItem}
              selectedSubItem={selectedSubMenuItem}
              onSelectMenuItem={handleOnSelect}
              subMenuOpen={subMenuOpen}
            />
            <SideMenuUserItems
              mobile={mobile}
              mobileMenuOpen={mobileMenuOpen}
              reduce={reduce}
              onCloseMobileMenu={onCloseMobileMenu}
              selectedItem={selectedMenuItem}
              selectedSubItem={selectedSubMenuItem}
              onSelectMenuItem={handleOnSelect}
              subMenuOpen={subMenuOpen}
            />

            {smDown && (
              <div className={styles["feedback-wrap"]}>
                <BorderButton href={FEEDBACK_LINK} target="_blanc" title={FEEDBACK_LINK}>{intl.formatMessage({ id: "send_feedback" })}</BorderButton>
              </div>
            )}
            {!mobile && (
              <>
                <Box className={styles["reduce-icon-wrap"]}>
                  <KeyboardTab
                    cursor="pointer"
                    fontSize="small"
                    sx={{ transform: `${reduce ? "" : "rotate(180deg)"}` }}
                    onClick={onReduce}
                  />
                </Box>
              </>
            )}
          </>
        )}
      </Box>
      {mobile && mobileMenuOpen && (
        <a onClick={handleOverlayOnClick}>
          <Box className={styles["sidebar-underlay"]} />
        </a>
      )}
    </>
  );
};

export default SideMenu;
